import HiddenInput from "@/components/form/components/HiddenInput";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { DatePicker, Form, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCommissionAdvanceCancellation } from "../../../../../common/api/queries";
import { ModalSizes } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { CommissionAdvance, CreateCommissionAdvanceCancellation } from "../../types";
import { MANUAL_COMMISSION_ADVANCE_CANCELLATION_REASONS } from "../../utils";

interface Props {
  contractId: UUID;
  advance: CommissionAdvance;
  isOpen: boolean;
  onCancel: VoidFunction;
}

export const CommissionAdvanceCancellationModalForm = ({ contractId, advance, isOpen, onCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateCommissionAdvanceCancellation>();

  const createCommissionAdvanceCancellation = useCreateCommissionAdvanceCancellation();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ optimisticLockVersion: advance.optimisticLockVersion });
    }
  }, [advance.optimisticLockVersion, form, isOpen]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      createCommissionAdvanceCancellation.mutate(
        { id: contractId, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => setErrorsToForm(form, "commissions.advance.attrs", error.violations),
        },
      );
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      title={t("commissions.advance.titles.createCancellation")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionAdvanceCancellation.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionAdvanceCancellationForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="manualCancellationReason"
          label={t("commissions.advance.enums.cancellationReason._label")}
          rules={[validations.notNull]}
        >
          <Select
            {...selectStandardProps}
            options={MANUAL_COMMISSION_ADVANCE_CANCELLATION_REASONS.map((reason) => ({
              value: reason,
              label: t(`commissions.advance.enums.cancellationReason.${reason}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="manualCancellationRequestedAt"
          label={t("commissions.advance.attrs.manualCancellationRequestedAt")}
          rules={[validations.notNull, validations.notFuture]}
          {...datePickerFormItemProps}
        >
          <DatePicker {...datePickerStandardProps} maxDate={dayjs()} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
